import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { Card, Column, DataTable, DataTableFilterMeta, DDBrands, FilterMatchMode, ProgressBar, SCIcon } from '../../Common/Imports';
import { ApiResponse, Brands_EXT, ImportData_EXT } from '../../Models/Classes';
import { ExcelExport } from '../../Common/Utility/ExcelExport';

export const DataImportNoBrand: React.FC = () => {
    const { t } = useTranslation();
    const [data, setData] = useState<ImportData_EXT[]>([]);
    const AxiosHelper = useAxios();
    const { showToast } = useToast();
    const [BrandsData, setBrandsData] = useState<Brands_EXT[] | undefined>();
    const [TableLoader, SetTableLoader] = useState<boolean>(false);
    const [TableFilters, setTableFilters] = useState<DataTableFilterMeta>({
        stakeholderName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        customerReservationCode: { value: null, matchMode: FilterMatchMode.CONTAINS },
        structureName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        structureAddress: { value: null, matchMode: FilterMatchMode.CONTAINS },
        brandName: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });

    const GetData = useCallback(async () => {
        SetTableLoader(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<ImportData_EXT[]>>(`Operative/GetListWithNoBrand`);
            if (resp.data.code === 0) {
                const processedData = resp.data.data.map(item => ({
                    ...item,
                    checkInDate: new Date(item.checkInDate),
                    checkOutDate: new Date(item.checkOutDate)
                }));
                setData(processedData);
            }
            const resp1 = await AxiosHelper.get<ApiResponse<Brands_EXT[]>>(`Brand/GetList`);
            if (resp1.data.code === 0) {
                setBrandsData(resp1.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            SetTableLoader(false);
        }
    }, [AxiosHelper]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const UpdateRowData = async (data: ImportData_EXT, e: number | undefined) => {
        if (e) {
            data.brandName = BrandsData?.find(m => m.idBrand === e)?.name ?? data.brandName;
            try {
                await AxiosHelper.post(`Imports/SaveSingle`, data);
                showToast('success', 'Success', t("Common.Toasts.Update.Success"));
                GetData();
            } catch (error: unknown) {
                console.log(error);
                showToast('error', 'Error', t("Common.Toasts.Update.Error"));
            }
        }
    }

    const setBrandBody = (data: ImportData_EXT) => {
        return (
            <DDBrands onChange={(e) => UpdateRowData(data, e)} IdStakeholder={data.idStakeholder} />
        );
    }

    const HeaderTemplate = (
        <div className="flex justify-content-between align-items-center pt-2 pe-2 ps-2">
            <div className="flex align-items-center">
                <SCIcon Name="Refresh" Pointer OnClick={() => GetData()} />
            </div>
            <div className="flex align-items-center">
                <SCIcon Name="Export" Pointer OnClick={() => ExcelExport(data, TableFilters, "EXPORT_Import")} />
            </div>
        </div>
    );

    return (
        <Card header={HeaderTemplate}>
            {
                TableLoader ?
                    <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
                    :
                    <>
                        <DataTable
                            value={data}
                            size="small"
                            stripedRows
                            paginator
                            rows={10}
                            onFilter={(e) => setTableFilters(e.filters)}
                            filters={TableFilters}
                            loading={data ? false : true}
                            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                            rowsPerPageOptions={[10, 20, 50]}
                            tableStyle={{ minWidth: '50rem' }}>
                            <Column
                                body={(e: ImportData_EXT) => setBrandBody(e)}
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="stakeholderName"
                                header={t("Imports.DataImportNoBrand.List.StakeholderName")}
                                sortable
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="customerReservationCode"
                                header={t("Imports.DataImportNoBrand.List.ReservationCode")}
                                sortable
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="structureName"
                                header={t("Imports.DataImportNoBrand.List.StructureName")}
                                sortable
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="structureAddress"
                                header={t("Imports.DataImportNoBrand.List.StructureAddress")}
                                sortable
                                bodyClassName="table-cell-nowrap" />
                            <Column
                                filter
                                field="brandName"
                                header={t("Imports.DataImportNoBrand.List.BrandName")}
                                sortable
                                bodyClassName="table-cell-nowrap" />
                        </DataTable>
                    </>
            }
        </Card >
    );
};

export default DataImportNoBrand;
